<template>
	<div class="view confirmation" v-if="invoice">
		<div class="view-title">
			<div class="collection">2019 - 2020<br>Black Pug Studio Collection</div>
			<h1><i class="ic ic-check"></i> Confirmation!</h1>
		</div>
		<div class="container">
			<div class="invoice">
				<div class="invoice-details">
					<div class="invoice-title">Invoice #{{invoice.order_id}}</div>
					<div class="invoice-message">
						<h3>Thank you</h3>
						<p>Payment successfully completed!<br>
						You should receive an email confirmation including your receipt.</p>
					</div>
					<div class="invoice-totals">
						<div class="invoice-line">
							<span>Subtotal</span>
							<i></i>
							<span>€{{invoice.subtotal}}</span>
						</div>
						<div class="invoice-line">
							<span>Tax</span>
							<i></i>
							<span>€{{invoice.vat}}</span>
						</div>
						<div class="invoice-line" v-if="invoice.coupon">
							<span>{{invoice.coupon.amount}}{{invoice.coupon.type === 'percent' ? '%' : '€'}} Discount Coupon</span>
							<i></i>
							<span>- €{{invoice.discount}}</span>
						</div>
						<div class="invoice-line">
							<span>Shipping & Handling</span>
							<i></i>
							<span>€{{invoice.shipping}}</span>
						</div>
					</div>
					<div class="invoice-total">
						<div class="total-label">Total</div>
						<div class="total-amount">€{{invoice.total}}</div>
					</div>
				</div>
				<div class="invoice-paid"><i class="ic ic-paw"></i>Paid</div>
			</div>
		</div>
			<router-link class="btn" to="/">Return to store <i class="ic ic-forward"></i></router-link>
	</div>
    <div class="view checkout" v-else-if="cart" :loading="loading">
		<div class="view-title">
			<div class="collection">2019 - 2020<br>Black Pug Studio Collection</div>
			<h1><i class="ic ic-checkout"></i> Checkout</h1>
		</div>
		<div class="container">
			<div class="steps" :has-error="error && step === 1">
				<div class="step" :current="step === 1" :past="step > 1">
					<div class="step-header">
						<div class="step-info">
							<h5 class="step-title"><i>1.</i>Shipping details</h5>
							<div class="step-details" v-if="step > 1">
								<span>{{orderModel.client.address1}}</span>
								<span v-if="orderModel.client.address2">, {{orderModel.client.address2}}</span>
								<span>, {{orderModel.client.city}}</span>
								<span>, {{getCountryName(orderModel.client.country_code)}}</span>
							</div>
						</div>
						<button class="btn" v-if="step > 1" @click="step = 1"><i class="ic ic-edit"></i> Edit</button>
					</div>
					<div class="step-content">
						<form @submit.prevent="getShipping" :loading="loading">
							<div class="error" v-if="error">{{error}}</div>
							<div class="fieldgroup">
								<div class="field">
									<label>Country</label>
									<select v-model="orderModel.client.country_code" required>
										<option v-for="country in countries" :value="country.code" :key="country.code">{{country.name}}</option>
									</select>
								</div>
							</div>
							<div class="fieldgroup">
								<div class="field m">
									<label>Full name</label>
									<input type="text" v-model="orderModel.client.name" maxlength="80" required>
								</div>
								<div class="field m">
									<label>Email</label>
									<input type="email" v-model="orderModel.client.email" maxlength="140" required>
								</div>
							</div>
							<div class="fieldgroup">
								<div class="field field-check">
									<button type="button" class="check" :checked="newsletter" @click="newsletter = !newsletter"></button>
									<span>Keep me up to date on news & exclusive offers!</span>
								</div>
							</div>
							<div class="fieldgroup">
								<div class="field">
									<label>Address Line 1</label>
									<input placeholder="Street and number, etc..." type="text" v-model="orderModel.client.address1" maxlength="255" required>
								</div>
							</div>
							<div class="fieldgroup">
								<div class="field">
									<label>Address Line 2 <i>(Optional)</i></label>
									<input placeholder="Apartment, suite, unit, etc..." type="text" v-model="orderModel.client.address2" maxlength="255">
								</div>
							</div>
							<div class="fieldgroup mobile">
								<div class="field s">
									<label>City</label>
									<input type="text" v-model="orderModel.client.city" maxlength="60" required>
								</div>
								<div class="field s">
									<label>State / Province</label>
									<input type="text" v-model="orderModel.client.state_name" maxlength="40" required>
								</div>
								<div class="field s" v-if="orderModel.client.country_code === 'US'">
									<label>State code</label>
									<input type="text" v-model="orderModel.client.state_code" maxlength="2" minlength="2" pattern="[A-Za-z]{2}" autocapitalize="characters" required>
								</div>
								<div class="field s">
									<label>Zip / Postal Code</label>
									<input type="text" v-model="orderModel.client.zip" maxlength="10" required>
								</div>
							</div>
							<div class="fieldgroup">
								<div class="field field-check">
									<button type="button" class="check" :checked="useShipping" @click="useShipping = !useShipping"></button>
									<span>Shipping address is the same as billing address</span>
								</div>
							</div>
							<!-- <div class="fieldgroup">
								<div class="field">
									<div id="card"></div>
								</div>
							</div> -->
							<div class="buttons">
								<button class="btn large" type="submit" :disabled="loading">{{loading ? 'Processing...' : 'Next'}}</button>
							</div>
						</form>
					</div>
				</div>
				<div class="step" :current="step === 2" :past="step > 2">
					<div class="step-header">
						<div class="step-info">
							<h5 class="step-title"><i>2.</i>Shipping method</h5>
							<div class="step-details" v-if="step > 2 && cart.shipping">
								<span>{{cart.shipping.name}} ({{cart.shipping.description}}) €{{cart.shipping.rate}}</span>
							</div>
						</div>
						<button class="btn" v-if="step > 2" @click="step = 2"><i class="ic ic-edit"></i> Edit</button>
					</div>
					<div class="step-content">
						<div class="shipping" v-if="methods.length">
							<div class="method" v-for="method in methods" :key="method.id" :selected="shipping === method.id">
								<div class="method-select">
									<button class="radio" :checked="shipping === method.id" @click="shipping = method.id"></button>
								</div>
								<div class="method-info">
									<div class="method-name">{{method.name}}</div>
									<div class="method-description">{{method.description}}</div>
								</div>
								<span class="method-rate">€{{method.rate}}</span>
							</div>
						</div>
						<div class="buttons">
							<button class="btn large" :disabled="!cart.shipping" @click="step = useShipping ? 4 : 3">Next</button>
						</div>
					</div>
				</div>
				<div class="step" :current="step === 3" :past="step > 3 || (step !== 3 && useShipping && step > 1)">
					<div class="step-header">
						<div class="step-info">
							<h5 class="step-title"><i>3.</i>Billing address</h5>
							<div class="step-details" v-if="step > 3 || (step !== 3 && useShipping && step > 1)">
								<span>{{orderModel.billing.address1}}</span>
								<span v-if="orderModel.billing.address2">, {{orderModel.billing.address2}}</span>
								<span>, {{orderModel.billing.city}}</span>
								<span>, {{getCountryName(orderModel.billing.country_code)}}</span>
							</div>
						</div>
						<button class="btn" v-if="step > 3" @click="step = 3"><i class="ic ic-edit"></i> Edit</button>
					</div>
					<div class="step-content">
						<form @submit.prevent="step = 4" :loading="loading">
							<div class="error" v-if="error">{{error}}</div>
							<div class="fieldgroup">
								<div class="field">
									<label>Country</label>
									<select v-model="orderModel.billing.country_code" required>
										<option v-for="country in countries" :value="country.code" :key="country.code">{{country.name}}</option>
									</select>
								</div>
							</div>
							<div class="fieldgroup">
								<div class="field m">
									<label>Full name</label>
									<input type="text" v-model="orderModel.billing.name" maxlength="80" required>
								</div>
								<div class="field m">
									<label>Email</label>
									<input type="email" v-model="orderModel.billing.email" maxlength="140" required>
								</div>
							</div>
							<div class="fieldgroup">
								<div class="field">
									<label>Address Line 1</label>
									<input placeholder="Street and number, etc..." type="text" v-model="orderModel.billing.address1" maxlength="255" required>
								</div>
							</div>
							<div class="fieldgroup">
								<div class="field">
									<label>Address Line 2 <i>(Optional)</i></label>
									<input placeholder="Apartment, suite, unit, etc..." type="text" v-model="orderModel.billing.address2" maxlength="255">
								</div>
							</div>
							<div class="fieldgroup mobile">
								<div class="field s">
									<label>City</label>
									<input type="text" v-model="orderModel.billing.city" maxlength="60" required>
								</div>
								<div class="field s">
									<label>State / Province</label>
									<input type="text" v-model="orderModel.billing.state_name" maxlength="40" required>
								</div>
								<div class="field s">
									<label>Zip / Postal Code</label>
									<input type="text" v-model="orderModel.billing.zip" maxlength="10" required>
								</div>
							</div>
							<div class="buttons">
								<button class="btn large" type="submit" :disabled="loading">{{loading ? 'Processing...' : 'Next'}}</button>
							</div>
						</form>
					</div>
				</div>
				<div class="step" :current="step === 4" :past="step > 4">
					<div class="step-header">
						<div class="step-info">
							<h5 class="step-title"><i>4.</i>Payment method</h5>
							<div class="step-details" v-if="step > 4">Valid payment card selected</div>
						</div>
						<button class="btn" v-if="step > 4" @click="step = 4"><i class="ic ic-edit"></i> Edit</button>
					</div>
					<div class="step-content">
						<div class="error" v-if="error">{{error}}</div>
						<div class="cards">
							<label>Supported Cards:</label>
							<div class="cards-list">
								<img src="@/assets/images/bps_card_visa.png" alt="Visa" class="card">
								<img src="@/assets/images/bps_card_mastercard.png" alt="Mastercard" class="card">
								<img src="@/assets/images/bps_card_ae.png" alt="American Express" class="card">
								<img src="@/assets/images/bps_card_jcb.png" alt="JCB" class="card">
								<img src="@/assets/images/bps_card_discover.png" alt="Discover" class="card">
								<img src="@/assets/images/bps_card_dc.png" alt="Diners Club" class="card">
							</div>
						</div>
						<form class="payment-form" @submit.prevent="getPaymentToken">
							<div class="fieldgroup">
								<div class="field m">
									<label>Credit/Debit Card Number</label>
									<div class="card-input" ref="cardNumber"></div>
								</div>
								<div class="field m">
									<label>Full Name</label>
									<input type="text" v-model="cardName" required>
								</div>
							</div>
							<div class="fieldgroup">
								<div class="field m">
									<div class="fieldgroup">
										<div class="field">
											<label>Expiry Date</label>
											<div class="card-input" ref="cardExpiry"></div>
										</div>
										<div class="field s">
											<label>Security Code</label>
											<div class="card-input" ref="cardCvc"></div>
										</div>
									</div>
								</div>
							</div>
							<div class="buttons">
								<button class="btn large" type="submit" :disabled="loading">{{loading ? 'Processing...' : 'Next'}}</button>
							</div>
						</form>
					</div>
				</div>
				<div class="step validation" :current="step === 5">
					<div class="step-header">
						<h5 class="step-title"><i>5.</i>Confirmation</h5>
					</div>
					<div class="step-content">
						<div class="error" v-if="error">{{error}}</div>
						<p>All right! You are all set and ready confirm your purchase. We are sure you can’t wait to get that gear as soon as possible.</p>
						<p>What are you waiting for!</p>
						<p>You can now confirm payment.</p>
						<div class="buttons">
							<button class="btn large btn-y" @click="submitOrder" :disabled="loading">
								<div>{{loading ? 'Processing...' : 'Pay now'}}</div>
								<div class="small" v-if="!loading">(and send me my gear!)</div>
							</button>
						</div>
					</div>
				</div>
			</div>
			<div class="summary" :has-coupon="Boolean(coupon)">
				<div class="discount-container">
					<div class="step-header">
						<h5><i class="ic ic-ticket"></i>Discount coupon</h5>
						<span>Enter your code below</span>
					</div>
					<div class="step-content">
						<div class="field">
							<label>Coupon code</label>
							<input type="text" @blur="submitCoupon" :disabled="coupon">
							<div class="note" v-if="coupon">Congratulations! You entered a valid {{coupon.amount}}{{coupon.type === 'percent' ? '%' : '€'}} discount coupon.</div>
						</div>
					</div>
				</div>
				<div class="step-header" @click="summaryOpened = !summaryOpened">
					<h5 class="step-title">Order Summary <span v-if="isMobile">({{cart.item_count}} item{{cart.item_count > 1 ? 's' : ''}})<i class="ic ic-chevron-bottom" :style="{transform: summaryOpened ? 'rotateZ(-180deg)' : 'none'}"></i></span></h5>
					<div class="step-total" v-if="isMobile">€{{cart.total}}</div>
				</div>
				<div ref="summaryContent" class="summary-content" :opened="summaryOpened">
					<div class="items" v-if="isMobile">
						<table>
							<tr v-for="product in cart.products" :key="product.id" class="item">
								<td class="item-image">
									<img @load="setSummarySize" :src="product.images[0].url" :alt="product.name">
								</td>
								<td class="item-info">
									<router-link class="item-name" :to="`/products/${product.details.id}`">{{product.details.name}}</router-link>
									<div class="item-variant" v-if="product.details.color">COLOR VARIATION: {{product.details.color}}</div>
									<div class="item-variant" v-if="product.details.size">SIZE: {{product.details.size}}</div>
									<div class="item-quantity">{{product.quantity}} x €{{product.retail_price}}</div>
								</td>
							</tr>
						</table>
					</div>
					<div class="total-line">
						<span>Subtotal</span>
						<span>€{{cart.total_excl_vat}}</span>
					</div>
					<div class="total-line">
						<span>Tax</span>
						<span>€{{cart.vat}}</span>
					</div>
					<div class="total-line discount-line" v-if="coupon">
						<span>{{coupon.amount}}{{coupon.type === 'percent' ? '%' : '€'}} Discount Coupon</span>
						<span>- €{{getDiscount(cart.total_excl_shipping, coupon)}}</span>
					</div>
					<div class="total-line">
						<span>Shipping & Handling</span>
						<span v-if="cart.shipping">€{{cart.shipping.rate}}</span>
						<span class="next-step" v-else>Calculated at next step</span>
					</div>
					<div class="total-line final">
						<span>Total</span>
						<span>€{{getTotalAfterDiscount(cart, coupon)}}</span>
					</div>
					<div class="step-header" v-if="!isMobile">
						<h5 class="step-title">Order items</h5>
						<router-link class="btn" to="/cart"><i class="ic ic-edit"></i> Edit</router-link>
					</div>
					<div class="items" v-if="!isMobile">
						<table>
							<tr v-for="product in cart.products" :key="product.id" class="item">
								<td class="item-image">
									<router-link :to="`/${product.details.slug}/${product.id}`">
										<img :src="product.images[0].url" :alt="product.name">
									</router-link>
								</td>
								<td class="item-info">
									<router-link class="item-name" :to="`/${product.details.slug}/${product.id}`">{{product.name}}</router-link>
									<div class="item-variant" v-if="product.details.color">COLOR VARIATION: {{product.details.color}}</div>
									<div class="item-variant" v-if="product.details.size">SIZE: {{product.details.size}}</div>
									<div class="item-quantity">{{product.quantity}} x €{{product.retail_price}}</div>
								</td>
							</tr>
						</table>
					</div>
					<router-link v-if="isMobile" to="/cart" class="btn"><i class="ic ic-edit"></i> Edit</router-link>
				</div>
			</div>
		</div>
		<router-link class="back-btn" to="/cart" v-if="isMobile"><i class="ic ic-chevron-left"></i> Return to cart</router-link>
    </div>
</template>

<script>
/* global Stripe */

import { mapGetters } from "vuex"

export default {
	name: "checkout",
    components: {},
    computed: {
        ...mapGetters({
			auth: 'getAuth',
			db: 'getDb',
			cart: 'getCart',
			functions: 'getFunctions',
			isMobile: 'getMobile'
		}),
    },
	data() {
		return {
			methods: [],
			shipping: '',
			cardName: '',
			summaryOpened: true,
			step: 1,
			apiUrl: 'https://bps-store.firebaseapp.com/api',
			orderModel: {
				client: {
					name: '',
					address1: '',
					address2: '',
					city: '',
					zip: '',
					email: '',
					country_code: '',
					state_name: '',
					state_code: ''
				},
				billing: {
					name: '',
					address1: '',
					address2: '',
					city: '',
					zip: '',
					email: '',
					country_code: '',
					state_name: ''
				},
				test: !window.location.host.includes('blackpugstudio.com'),
				coupon_code: null
			},
			loading: false,
			useShipping: false,
			newsletter: false,
			error: null,
			stripe: null,
			card: null,
			invoice: null,
			countries: [
				{ "name": "Afghanistan", "dial_code": "+93", "code": "AF" }, { "name": "Åland Islands", "dial_code": "+358", "code": "AX" }, { "name": "Albania", "dial_code": "+355", "code": "AL" }, { "name": "Algeria", "dial_code": "+213", "code": "DZ" }, { "name": "American Samoa", "dial_code": "+1684", "code": "AS" }, { "name": "Andorra", "dial_code": "+376", "code": "AD" }, { "name": "Angola", "dial_code": "+244", "code": "AO" }, { "name": "Anguilla", "dial_code": "+1264", "code": "AI" }, { "name": "Antarctica", "dial_code": "+672", "code": "AQ" }, { "name": "Antigua and Barbuda", "dial_code": "+1268", "code": "AG" }, { "name": "Argentina", "dial_code": "+54", "code": "AR" }, { "name": "Armenia", "dial_code": "+374", "code": "AM" }, { "name": "Aruba", "dial_code": "+297", "code": "AW" }, { "name": "Australia", "dial_code": "+61", "code": "AU" }, { "name": "Austria", "dial_code": "+43", "code": "AT" }, { "name": "Azerbaijan", "dial_code": "+994", "code": "AZ" }, { "name": "Bahamas", "dial_code": "+1242", "code": "BS" }, { "name": "Bahrain", "dial_code": "+973", "code": "BH" }, { "name": "Bangladesh", "dial_code": "+880", "code": "BD" }, { "name": "Barbados", "dial_code": "+1246", "code": "BB" }, { "name": "Belarus", "dial_code": "+375", "code": "BY" }, { "name": "Belgium", "dial_code": "+32", "code": "BE" }, { "name": "Belize", "dial_code": "+501", "code": "BZ" }, { "name": "Benin", "dial_code": "+229", "code": "BJ" }, { "name": "Bermuda", "dial_code": "+1441", "code": "BM" }, { "name": "Bhutan", "dial_code": "+975", "code": "BT" }, { "name": "Bolivia, Plurinational State of bolivia", "dial_code": "+591", "code": "BO" }, { "name": "Bosnia and Herzegovina", "dial_code": "+387", "code": "BA" }, { "name": "Botswana", "dial_code": "+267", "code": "BW" }, { "name": "Bouvet Island", "dial_code": "+47", "code": "BV" }, { "name": "Brazil", "dial_code": "+55", "code": "BR" }, { "name": "British Indian Ocean Territory", "dial_code": "+246", "code": "IO" }, { "name": "Brunei Darussalam", "dial_code": "+673", "code": "BN" }, { "name": "Bulgaria", "dial_code": "+359", "code": "BG" }, { "name": "Burkina Faso", "dial_code": "+226", "code": "BF" }, { "name": "Burundi", "dial_code": "+257", "code": "BI" }, { "name": "Cambodia", "dial_code": "+855", "code": "KH" }, { "name": "Cameroon", "dial_code": "+237", "code": "CM" }, { "name": "Canada", "dial_code": "+1", "code": "CA" }, { "name": "Cape Verde", "dial_code": "+238", "code": "CV" }, { "name": "Cayman Islands", "dial_code": "+ 345", "code": "KY" }, { "name": "Central African Republic", "dial_code": "+236", "code": "CF" }, { "name": "Chad", "dial_code": "+235", "code": "TD" }, { "name": "Chile", "dial_code": "+56", "code": "CL" }, { "name": "China", "dial_code": "+86", "code": "CN" }, { "name": "Christmas Island", "dial_code": "+61", "code": "CX" }, { "name": "Cocos (Keeling) Islands", "dial_code": "+61", "code": "CC" }, { "name": "Colombia", "dial_code": "+57", "code": "CO" }, { "name": "Comoros", "dial_code": "+269", "code": "KM" }, { "name": "Congo", "dial_code": "+242", "code": "CG" }, { "name": "Congo, The Democratic Republic of the Congo", "dial_code": "+243", "code": "CD" }, { "name": "Cook Islands", "dial_code": "+682", "code": "CK" }, { "name": "Costa Rica", "dial_code": "+506", "code": "CR" }, { "name": "Cote d'Ivoire", "dial_code": "+225", "code": "CI" }, { "name": "Croatia", "dial_code": "+385", "code": "HR" }, { "name": "Cuba", "dial_code": "+53", "code": "CU" }, { "name": "Cyprus", "dial_code": "+357", "code": "CY" }, { "name": "Czech Republic", "dial_code": "+420", "code": "CZ" }, { "name": "Denmark", "dial_code": "+45", "code": "DK" }, { "name": "Djibouti", "dial_code": "+253", "code": "DJ" }, { "name": "Dominica", "dial_code": "+1767", "code": "DM" }, { "name": "Dominican Republic", "dial_code": "+1849", "code": "DO" }, { "name": "Ecuador", "dial_code": "+593", "code": "EC" }, { "name": "Egypt", "dial_code": "+20", "code": "EG" }, { "name": "El Salvador", "dial_code": "+503", "code": "SV" }, { "name": "Equatorial Guinea", "dial_code": "+240", "code": "GQ" }, { "name": "Eritrea", "dial_code": "+291", "code": "ER" }, { "name": "Estonia", "dial_code": "+372", "code": "EE" }, { "name": "Ethiopia", "dial_code": "+251", "code": "ET" }, { "name": "Falkland Islands (Malvinas)", "dial_code": "+500", "code": "FK" }, { "name": "Faroe Islands", "dial_code": "+298", "code": "FO" }, { "name": "Fiji", "dial_code": "+679", "code": "FJ" }, { "name": "Finland", "dial_code": "+358", "code": "FI" }, { "name": "France", "dial_code": "+33", "code": "FR" }, { "name": "French Guiana", "dial_code": "+594", "code": "GF" }, { "name": "French Polynesia", "dial_code": "+689", "code": "PF" }, { "name": "French Southern Territories", "dial_code": "+262", "code": "TF" }, { "name": "Gabon", "dial_code": "+241", "code": "GA" }, { "name": "Gambia", "dial_code": "+220", "code": "GM" }, { "name": "Georgia", "dial_code": "+995", "code": "GE" }, { "name": "Germany", "dial_code": "+49", "code": "DE" }, { "name": "Ghana", "dial_code": "+233", "code": "GH" }, { "name": "Gibraltar", "dial_code": "+350", "code": "GI" }, { "name": "Greece", "dial_code": "+30", "code": "GR" }, { "name": "Greenland", "dial_code": "+299", "code": "GL" }, { "name": "Grenada", "dial_code": "+1473", "code": "GD" }, { "name": "Guadeloupe", "dial_code": "+590", "code": "GP" }, { "name": "Guam", "dial_code": "+1671", "code": "GU" }, { "name": "Guatemala", "dial_code": "+502", "code": "GT" }, { "name": "Guernsey", "dial_code": "+44", "code": "GG" }, { "name": "Guinea", "dial_code": "+224", "code": "GN" }, { "name": "Guinea-Bissau", "dial_code": "+245", "code": "GW" }, { "name": "Guyana", "dial_code": "+592", "code": "GY" }, { "name": "Haiti", "dial_code": "+509", "code": "HT" }, { "name": "Heard Island and Mcdonald Islands", "dial_code": "+0", "code": "HM" }, { "name": "Holy See (Vatican City State)", "dial_code": "+379", "code": "VA" }, { "name": "Honduras", "dial_code": "+504", "code": "HN" }, { "name": "Hong Kong", "dial_code": "+852", "code": "HK" }, { "name": "Hungary", "dial_code": "+36", "code": "HU" }, { "name": "Iceland", "dial_code": "+354", "code": "IS" }, { "name": "India", "dial_code": "+91", "code": "IN" }, { "name": "Indonesia", "dial_code": "+62", "code": "ID" }, { "name": "Iran, Islamic Republic of Persian Gulf", "dial_code": "+98", "code": "IR" }, { "name": "Iraq", "dial_code": "+964", "code": "IQ" }, { "name": "Ireland", "dial_code": "+353", "code": "IE" }, { "name": "Isle of Man", "dial_code": "+44", "code": "IM" }, { "name": "Israel", "dial_code": "+972", "code": "IL" }, { "name": "Italy", "dial_code": "+39", "code": "IT" }, { "name": "Jamaica", "dial_code": "+1876", "code": "JM" }, { "name": "Japan", "dial_code": "+81", "code": "JP" }, { "name": "Jersey", "dial_code": "+44", "code": "JE" }, { "name": "Jordan", "dial_code": "+962", "code": "JO" }, { "name": "Kazakhstan", "dial_code": "+7", "code": "KZ" }, { "name": "Kenya", "dial_code": "+254", "code": "KE" }, { "name": "Kiribati", "dial_code": "+686", "code": "KI" }, { "name": "Korea, Democratic People's Republic of Korea", "dial_code": "+850", "code": "KP" }, { "name": "Korea, Republic of South Korea", "dial_code": "+82", "code": "KR" }, { "name": "Kosovo", "dial_code": "+383", "code": "XK" }, { "name": "Kuwait", "dial_code": "+965", "code": "KW" }, { "name": "Kyrgyzstan", "dial_code": "+996", "code": "KG" }, { "name": "Laos", "dial_code": "+856", "code": "LA" }, { "name": "Latvia", "dial_code": "+371", "code": "LV" }, { "name": "Lebanon", "dial_code": "+961", "code": "LB" }, { "name": "Lesotho", "dial_code": "+266", "code": "LS" }, { "name": "Liberia", "dial_code": "+231", "code": "LR" }, { "name": "Libyan Arab Jamahiriya", "dial_code": "+218", "code": "LY" }, { "name": "Liechtenstein", "dial_code": "+423", "code": "LI" }, { "name": "Lithuania", "dial_code": "+370", "code": "LT" }, { "name": "Luxembourg", "dial_code": "+352", "code": "LU" }, { "name": "Macao", "dial_code": "+853", "code": "MO" }, { "name": "Macedonia", "dial_code": "+389", "code": "MK" }, { "name": "Madagascar", "dial_code": "+261", "code": "MG" }, { "name": "Malawi", "dial_code": "+265", "code": "MW" }, { "name": "Malaysia", "dial_code": "+60", "code": "MY" }, { "name": "Maldives", "dial_code": "+960", "code": "MV" }, { "name": "Mali", "dial_code": "+223", "code": "ML" }, { "name": "Malta", "dial_code": "+356", "code": "MT" }, { "name": "Marshall Islands", "dial_code": "+692", "code": "MH" }, { "name": "Martinique", "dial_code": "+596", "code": "MQ" }, { "name": "Mauritania", "dial_code": "+222", "code": "MR" }, { "name": "Mauritius", "dial_code": "+230", "code": "MU" }, { "name": "Mayotte", "dial_code": "+262", "code": "YT" }, { "name": "Mexico", "dial_code": "+52", "code": "MX" }, { "name": "Micronesia, Federated States of Micronesia", "dial_code": "+691", "code": "FM" }, { "name": "Moldova", "dial_code": "+373", "code": "MD" }, { "name": "Monaco", "dial_code": "+377", "code": "MC" }, { "name": "Mongolia", "dial_code": "+976", "code": "MN" }, { "name": "Montenegro", "dial_code": "+382", "code": "ME" }, { "name": "Montserrat", "dial_code": "+1664", "code": "MS" }, { "name": "Morocco", "dial_code": "+212", "code": "MA" }, { "name": "Mozambique", "dial_code": "+258", "code": "MZ" }, { "name": "Myanmar", "dial_code": "+95", "code": "MM" }, { "name": "Namibia", "dial_code": "+264", "code": "NA" }, { "name": "Nauru", "dial_code": "+674", "code": "NR" }, { "name": "Nepal", "dial_code": "+977", "code": "NP" }, { "name": "Netherlands", "dial_code": "+31", "code": "NL" }, { "name": "Netherlands Antilles", "dial_code": "+599", "code": "AN" }, { "name": "New Caledonia", "dial_code": "+687", "code": "NC" }, { "name": "New Zealand", "dial_code": "+64", "code": "NZ" }, { "name": "Nicaragua", "dial_code": "+505", "code": "NI" }, { "name": "Niger", "dial_code": "+227", "code": "NE" }, { "name": "Nigeria", "dial_code": "+234", "code": "NG" }, { "name": "Niue", "dial_code": "+683", "code": "NU" }, { "name": "Norfolk Island", "dial_code": "+672", "code": "NF" }, { "name": "Northern Mariana Islands", "dial_code": "+1670", "code": "MP" }, { "name": "Norway", "dial_code": "+47", "code": "NO" }, { "name": "Oman", "dial_code": "+968", "code": "OM" }, { "name": "Pakistan", "dial_code": "+92", "code": "PK" }, { "name": "Palau", "dial_code": "+680", "code": "PW" }, { "name": "Palestinian Territory, Occupied", "dial_code": "+970", "code": "PS" }, { "name": "Panama", "dial_code": "+507", "code": "PA" }, { "name": "Papua New Guinea", "dial_code": "+675", "code": "PG" }, { "name": "Paraguay", "dial_code": "+595", "code": "PY" }, { "name": "Peru", "dial_code": "+51", "code": "PE" }, { "name": "Philippines", "dial_code": "+63", "code": "PH" }, { "name": "Pitcairn", "dial_code": "+64", "code": "PN" }, { "name": "Poland", "dial_code": "+48", "code": "PL" }, { "name": "Portugal", "dial_code": "+351", "code": "PT" }, { "name": "Puerto Rico", "dial_code": "+1939", "code": "PR" }, { "name": "Qatar", "dial_code": "+974", "code": "QA" }, { "name": "Romania", "dial_code": "+40", "code": "RO" }, { "name": "Russia", "dial_code": "+7", "code": "RU" }, { "name": "Rwanda", "dial_code": "+250", "code": "RW" }, { "name": "Reunion", "dial_code": "+262", "code": "RE" }, { "name": "Saint Barthelemy", "dial_code": "+590", "code": "BL" }, { "name": "Saint Helena, Ascension and Tristan Da Cunha", "dial_code": "+290", "code": "SH" }, { "name": "Saint Kitts and Nevis", "dial_code": "+1869", "code": "KN" }, { "name": "Saint Lucia", "dial_code": "+1758", "code": "LC" }, { "name": "Saint Martin", "dial_code": "+590", "code": "MF" }, { "name": "Saint Pierre and Miquelon", "dial_code": "+508", "code": "PM" }, { "name": "Saint Vincent and the Grenadines", "dial_code": "+1784", "code": "VC" }, { "name": "Samoa", "dial_code": "+685", "code": "WS" }, { "name": "San Marino", "dial_code": "+378", "code": "SM" }, { "name": "Sao Tome and Principe", "dial_code": "+239", "code": "ST" }, { "name": "Saudi Arabia", "dial_code": "+966", "code": "SA" }, { "name": "Senegal", "dial_code": "+221", "code": "SN" }, { "name": "Serbia", "dial_code": "+381", "code": "RS" }, { "name": "Seychelles", "dial_code": "+248", "code": "SC" }, { "name": "Sierra Leone", "dial_code": "+232", "code": "SL" }, { "name": "Singapore", "dial_code": "+65", "code": "SG" }, { "name": "Slovakia", "dial_code": "+421", "code": "SK" }, { "name": "Slovenia", "dial_code": "+386", "code": "SI" }, { "name": "Solomon Islands", "dial_code": "+677", "code": "SB" }, { "name": "Somalia", "dial_code": "+252", "code": "SO" }, { "name": "South Africa", "dial_code": "+27", "code": "ZA" }, { "name": "South Sudan", "dial_code": "+211", "code": "SS" }, { "name": "South Georgia and the South Sandwich Islands", "dial_code": "+500", "code": "GS" }, { "name": "Spain", "dial_code": "+34", "code": "ES" }, { "name": "Sri Lanka", "dial_code": "+94", "code": "LK" }, { "name": "Sudan", "dial_code": "+249", "code": "SD" }, { "name": "Suriname", "dial_code": "+597", "code": "SR" }, { "name": "Svalbard and Jan Mayen", "dial_code": "+47", "code": "SJ" }, { "name": "Swaziland", "dial_code": "+268", "code": "SZ" }, { "name": "Sweden", "dial_code": "+46", "code": "SE" }, { "name": "Switzerland", "dial_code": "+41", "code": "CH" }, { "name": "Syrian Arab Republic", "dial_code": "+963", "code": "SY" }, { "name": "Taiwan", "dial_code": "+886", "code": "TW" }, { "name": "Tajikistan", "dial_code": "+992", "code": "TJ" }, { "name": "Tanzania, United Republic of Tanzania", "dial_code": "+255", "code": "TZ" }, { "name": "Thailand", "dial_code": "+66", "code": "TH" }, { "name": "Timor-Leste", "dial_code": "+670", "code": "TL" }, { "name": "Togo", "dial_code": "+228", "code": "TG" }, { "name": "Tokelau", "dial_code": "+690", "code": "TK" }, { "name": "Tonga", "dial_code": "+676", "code": "TO" }, { "name": "Trinidad and Tobago", "dial_code": "+1868", "code": "TT" }, { "name": "Tunisia", "dial_code": "+216", "code": "TN" }, { "name": "Turkey", "dial_code": "+90", "code": "TR" }, { "name": "Turkmenistan", "dial_code": "+993", "code": "TM" }, { "name": "Turks and Caicos Islands", "dial_code": "+1649", "code": "TC" }, { "name": "Tuvalu", "dial_code": "+688", "code": "TV" }, { "name": "Uganda", "dial_code": "+256", "code": "UG" }, { "name": "Ukraine", "dial_code": "+380", "code": "UA" }, { "name": "United Arab Emirates", "dial_code": "+971", "code": "AE" }, { "name": "United Kingdom", "dial_code": "+44", "code": "GB" }, { "name": "United States", "dial_code": "+1", "code": "US" }, { "name": "Uruguay", "dial_code": "+598", "code": "UY" }, { "name": "Uzbekistan", "dial_code": "+998", "code": "UZ" }, { "name": "Vanuatu", "dial_code": "+678", "code": "VU" }, { "name": "Venezuela, Bolivarian Republic of Venezuela", "dial_code": "+58", "code": "VE" }, { "name": "Vietnam", "dial_code": "+84", "code": "VN" }, { "name": "Virgin Islands, British", "dial_code": "+1284", "code": "VG" }, { "name": "Virgin Islands, U.S.", "dial_code": "+1340", "code": "VI" }, { "name": "Wallis and Futuna", "dial_code": "+681", "code": "WF" }, { "name": "Yemen", "dial_code": "+967", "code": "YE" }, { "name": "Zambia", "dial_code": "+260", "code": "ZM" }, { "name": "Zimbabwe", "dial_code": "+263", "code": "ZW" }
			],
			coupon: null
		}
	},
	watch: {
		shipping() {
			const shipping = this.methods.find(method => method.id === this.shipping)
			if(shipping) this.$store.commit('setShipping', shipping)
		},
		step() {
			if(this.step === 4) {
				const elements = this.stripe.elements()
				const style = {
					base: {
						color: "#221f1f",
						fontFamily: '"overpass", "Helvetica Neue", Helvetica, sans-serif',
						fontSmoothing: "antialiased",
						fontSize: "14px",
						"::placeholder": {
							color: "rgba(34, 31, 31, 0.46)"
						}
					},
					invalid: {
						color: "#fa755a",
						iconColor: "#fa755a"
					}
				}
				this.cardNumber = elements.create("cardNumber", { style })
				this.cardNumber.mount(this.$refs.cardNumber)
				this.cardExpiry = elements.create("cardExpiry", { style })
				this.cardExpiry.mount(this.$refs.cardExpiry)
				this.cardCvc = elements.create("cardCvc", { style })
				this.cardCvc.mount(this.$refs.cardCvc, { style })
			}
			else {
				if(this.cardNumber) this.cardNumber.unmount()
				if(this.cardExpiry) this.cardExpiry.unmount()
				if(this.cardCvc) this.cardCvc.unmount()
			}
			if(this.step > 1) {
				this.$nextTick(() => {
					const s = document.querySelector('.step[current]')
					if(s) {
						this.scrollToY(s.offsetTop)
					}
				})
			}
			if(typeof window.ga === 'function') {
				const action = {'step': this.step}
				window.ga('ec:setAction', 'checkout_option', action)
				window.ga('send', 'event', 'Checkout', 'Option')
			}
		},
	},
	methods: {
		submitCoupon(e) {
			const target = e.target
			const value = target.value
			target.removeAttribute('error')
			target.parentNode.removeAttribute('loading')
			if(value) {
				target.parentNode.setAttribute('loading', '')
				this.functions.httpsCallable('validateCoupon')({code: value})
					.then(coupon => {
						this.coupon = coupon.data
						this.orderModel.coupon_code = coupon.data.code
						target.parentNode.removeAttribute('loading')
					})
					.catch(err => {
						console.log(err)
						target.setAttribute('error', '')
						target.parentNode.removeAttribute('loading')
					})
			}
		},
		getDiscount(total, coupon) {
			if(coupon.type === 'percent') return (parseFloat(total) * (coupon.amount / 100)).toFixed(2)
			return '0.00'
		},
		getTotalAfterDiscount(cart, coupon) {
			if(cart && coupon && coupon.type === 'percent') return (parseFloat(cart.total) - (parseFloat(this.getDiscount(cart.total_excl_shipping, coupon)))).toFixed(2)
			return cart.total
		},
		getCountryName(code) {
			const country = this.countries.find(c => c.code === code)
			return country ? country.name : ''
		},
		formatPrice(p) {
			if (isNaN(p)) return "0.00"
			return parseFloat(p).toFixed(2)
		},
		getShipping() {
			if(this.loading) return false
			this.loading = true
			this.error = null
			return this.auth().signInAnonymously()
				.then(user => {
					return this.functions.httpsCallable('getShipping')({
						items: this.cart.items,
						client: this.orderModel.client,
					})
				})
				.then(res => {
					// eslint-disable-next-line
					console.log(res.data)
					this.error = null
					this.loading = false
					this.orderModel.billing = this.useShipping ? Object.assign({}, this.orderModel.client) : {
						name: '',
						address1: '',
						address2: '',
						city: '',
						zip: '',
						email: '',
						country_code: '',
						state_name: ''
					}
					this.step = 2
					this.methods = res.data.map(m => {
						const match = m.name.match(/^(.*?) \((.*?)\)$/)
						if(match) {
							m.name = match[1]
							m.description = match[2]
						}
						return m
					}) || []
				})
				.catch(err => {
					if(typeof err.json === 'function') {
						err.json().then(error => {
							// eslint-disable-next-line
							console.log(error)
							this.error = error ? error.message || error : "Unknown error"
						})
					}
					else {
						// eslint-disable-next-line
						console.log(err)
						this.error = err ? err.message || err : "Unknow error"
					}
					this.loading = false
				})
		},
		getPaymentToken() {
			if(this.loading) return false
			this.loading = true
			this.error = null
			this.stripe.createToken(this.cardNumber, {
				name: this.cardName,
				address_line1: this.orderModel.billing.address1,
				address_line2: this.orderModel.billing.address2,
				address_city: this.orderModel.billing.city,
				address_state: this.orderModel.billing.state_name,
				address_zip: this.orderModel.billing.zip,
				address_country: this.orderModel.billing.country_code,
				currency: "eur"
			})
			.then(data => {
				console.log(data)
				if(data.error) throw data.error
				this.paymentToken = data.token
				this.loading = false
				this.step = 5
			})
			.catch(err => {
				console.log(err)
				this.error = (err && err.message) || 'Unknown error'
				this.loading = false
			})
		},
		submitOrder() {
			if(this.loading) return false
			this.loading = true
			this.error = null
			const order = Object.assign({
				payment_token: this.paymentToken.id,
				items: this.cart.items,
				shipping_method: this.cart.shipping.id,
				total_cart: this.getTotalAfterDiscount(this.cart, this.coupon),
				newsletter: this.newsletter
			}, this.orderModel)
			this.functions.httpsCallable('submitOrder')(order)
			.then(res => {
				// eslint-disable-next-line
				console.log(res.data)
				this.loading = false
				this.invoice = res.data
				if(typeof window.ga === 'function') {
					this.cart.products.map((product) => {
						window.ga('ec:addProduct', {
							id: product.details.id,
							name: product.details.name,
							category: product.details.category,
							variant: product.name,
							price: product.retail_price,
							quantity: product.quantity
						})
					})
					window.ga('ec:setAction', 'purchase', {
						'id': this.invoice.order_id,
						'affiliation': 'Black Pug Studio Merch Store',
						'revenue': this.invoice.total,
						'tax': this.invoice.vat,
						'shipping': this.invoice.shipping,
						'coupon': this.invoice.coupon ? this.invoice.coupon.code : null
					})
					window.ga('set', 'page', this.$route.path)
					window.ga('send', 'pageview')
				}
				this.$store.commit('emptyCart')
				this.scrollToY(0)
				if(order.newsletter && window.localStorage) window.localStorage.setItem('newsletter_sub', '1')
			})
			.catch(err => {
				// eslint-disable-next-line
				console.log(err)
				this.error = err ? err.message || err : "Unknown error"
				this.orderModel.nonce = this.db().collection('keys').doc().id
				this.loading = false
			})
		},
		setSummarySize() {
			if(this.isMobile) {
				setTimeout(() => {
					this.$refs.summaryContent.style.height = this.$refs.summaryContent.clientHeight + 'px'
				}, 250)
			}
		}
	},
	mounted: function() {
		if(!this.cart || !this.cart.items.length) return this.$router.push('/cart')
		this.orderModel.nonce = this.db().collection('keys').doc().id
		this.stripe = Stripe(this.orderModel.test ? "pk_test_GDEz4mI1Yge7AA6PcQgaMQUB" : "pk_live_LFH1vGfsg8MWUfZ57mV9OVbI")
		this.setSummarySize()
		if(typeof window.ga === 'function') {
			this.cart.products.map((product, i) => {
				window.ga('ec:addImpression', {
					id: product.details.id,
					name: product.details.name,
					category: product.details.category,
					variant: product.name,
					position: i + 1
				})
			})
			window.ga('ec:setAction', 'checkout', { 'step': 1 })
			window.ga('set', 'page', this.$route.path)
			window.ga('send', 'pageview')
		}
	},
	beforeDestroy: function() {
		if(this.cardNumber) this.cardNumber.unmount()
		if(this.cardExpiry) this.cardExpiry.unmount()
		if(this.cardCvc) this.cardCvc.unmount()
	}
}
</script>

<style scoped>
.view[loading]:after {
	content: '';
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 9999;
}

.view .view-title h1 {
	display: flex;
	justify-content: center;
	align-items: center;
}

.view .view-title h1 .ic {
	margin-right: 8px;
}

.container {
	position: relative;
	display: flex;
	justify-content: center;
	margin-top: .93vh;
	z-index: 3;
}

.invoice {
	position: relative;
	width: 24vw;
	background: #fff;
	color: #221F1F;
	text-align: center;
	margin-top: 4.58vh;
}

.invoice .invoice-details {
	padding: 2.31vh 3.125vw 1.48vh;
}

.invoice .invoice-title {
	font-weight: bold;
	letter-spacing: .1em;
	font-size: 1.66vh;
	text-transform: uppercase;
}

.invoice .invoice-title:after {
	content: '';
	display: block;
	width: 36px;
	height: 2px;
	background: #208E7F;
	margin: .48vh auto 1.85vh;
}

.invoice h3 {
	font-size: 2.59vh;
	font-weight: 900;
	color: #208E7F;
	text-transform: uppercase;
	line-height: 1;
	margin-bottom: 1.1vh;
}

.invoice .invoice-totals {
	margin-top: 4.16vh;
	margin-bottom: 1.85vh;
}

.invoice .invoice-line {
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	line-height: 1;
	margin-bottom: .93vh;
}

.invoice .invoice-line i {
	display: block;
	height: 1px;
	background: #221F1F;
	opacity: .12;
	flex-grow: 1;
	margin: 0 4px .12em;
}

.invoice .invoice-total .total-label {
	text-transform: uppercase;
	font-size: .93vh;
	letter-spacing: .2em;
}

.invoice .invoice-total .total-amount {
	font-size: 1.85vh;
	font-weight: 900;
	color: #EDBA52;
	margin-top: .2vh;
}

.invoice .invoice-paid {
	display: flex;
	align-items: center;
	justify-content: center;
	background: #208E7F;
	color: #fff;
	line-height: 5.55vh;
	letter-spacing: .1em;
	font-weight: 900;
	text-transform: uppercase;
	font-size: 1.66vh;
}

.invoice .invoice-paid .ic {
	width: 2.77vh;
	height: 2.77vh;
	margin-right: 8px;
}

.fieldgroup {
	display: flex;
	justify-content: space-between;
	margin:0 -4px 1.65vh;
}

.payment-form .fieldgroup {
	margin-bottom: 1.11vh;
}

.fieldgroup .field {
	width: 100%;
	margin: 0 4px;
	font-family: 'Inter';
}

.fieldgroup .field.m {
	width: 50%;
}

.fieldgroup .field.field-check {
	display: flex;
	align-items: center;
}

.fieldgroup .field.s {
	width: 45%;
}

.field label {
	display: block;
	margin-bottom: 4px;
	white-space: nowrap;
}

.field input,
.field select,
.card-input {
	width: 100%;
	height: 3.5vh;
	border-radius: 0;
	-webkit-appearance: none;
	background: #fff;
	color:#221f1f;
	border: none;
	font: inherit;
	padding: 0 20px;
}

.card-input {
	padding-top: 1.11vh;
	cursor: text;
}

.btn {
	display: inline-block;
	border: 4px solid #155C52;
	background: #208E7F;
	text-transform: uppercase;
	font-size: 1.11vh;
	font-weight: 600;
	padding: .25em .6em;
	text-align: center;
}

.btn.btn-y {
	background: #EDBA52;
	border-color: #D4A64A;
}

.btn.large {
	width: 17.7vw;
	height: 5.74vh;
	font-size: 1.85vh;
	font-weight: 900;
}

.btn.large .small {
	font-size: 1.11vh;
	font-weight: 400;
	text-transform: none;
	font-family: 'Inter';
}

.check {
	position: relative;
	width: 1.66vh;
	height: 1.66vh;
	vertical-align: middle;
	background: #fff;
	border: none;
	border-radius: 0;
	padding: 0;
	margin-right: 8px;
}

.check:after {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	opacity: 0;
	background: #208E7F url(../assets/images/bps_i_check-x.svg) no-repeat center/50% 50%;
	transition: opacity .16s ease-out;
}

.check[checked]:after {
	opacity: 1;
}

.summary {
	display: flex;
	flex-flow: column nowrap;
	width: 22.29vw;
	margin-left: 2.08vw;
}

.summary .discount-line {
	color: #32DBC5;
}

.summary .total-line {
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-size: 1.85vh;
	line-height: 1;
	padding: .55vh 0;
}

.summary .total-line span {
	display: inline-block;
}

.summary .total-line .next-step {
	font-size: 1.11vh;
}

.summary .total-line.final {
	color: #EDBA52;
	margin-bottom: 1.85vh;
	font-weight: 600;
}

.summary .total-line:not(:first-child) {
	border-top: 1px solid rgba(32, 142, 127, 0.46);
}

.summary .items {
	position: relative;
	height: 36.2vh;
	overflow: auto;
}

@media screen and (min-width: 1024px) {
	.summary .summary-content {
		display: flex;
		flex-flow: column nowrap;
		flex-grow: 1;
	}
	.summary[has-coupon] .items {
		height: 31.2vh;
	}
}

.steps {
	display: flex;
	flex-flow: column nowrap;
	width: 30.94vw;
	height: 69.07vh;
}

.steps[has-error] {
	height: 72.27vh;
}

.steps .step {
	display: flex;
	flex-flow: column nowrap;
	height: 3.7vh;
	margin-bottom: 2px;
	flex-shrink: 0;
	flex-grow: 0;
	position: relative;
	opacity: .46;
	transition: all .32s ease-out;
}

.steps .step[current] {
	opacity: 1;
	/* flex-grow: 1; */
	height: auto;
}

.step .step-content {
	position: relative;
	overflow: hidden;
	box-sizing: initial;
	flex-grow: 1;
}

.step[current] .step-content {
	padding: 1.11vh 0 1.66vh;
}

.step.validation[current] .step-content {
	padding-top: 1.85vh;
}

.step-header {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: #208E7F;
	color: #fff;
	height: 3.7vh;
	padding: 0 .42vw 0 1.07vw;
	flex-shrink: 0;
}

.discount-container .field {
	position: relative;
}

.discount-container .field[loading]:after {
  content: '';
  position: absolute;
  right: 5%;
  bottom: 1.4vh;
  width: 16px;
  height: 16px;
  border-top: 2px solid #208E7F;
  border-radius: 50%;
  animation-name: spin;
  animation-duration: .64s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  z-index: 3;
}

@keyframes spin {
  from {transform:rotate(0deg);}
  to {transform:rotate(360deg);}
}

.discount-container {
	margin-bottom: 2.22vh;
}

.discount-container input {
	padding: 0 16px;
	margin-bottom: 4px;
}

.discount-container input[error] {
	background: #e29b97 url(../assets/images/bps_i_warning.svg) no-repeat 95% center/2.22vh 2.22vh;
}

.discount-container input[disabled] {
	border: 5px solid #32DBC5;
	font-weight: bold;
	color: #fff;
	background: #208E7F url(../assets/images/bps_i_check-full-c.svg) no-repeat 95% center/1.48vh 1.48vh;
	-webkit-text-fill-color: #fff;
	opacity: 1;
}

.discount-container .step-header {
	margin-bottom: 1.11vh;
	justify-content: flex-start;
	line-height: 1;
}

.discount-container .step-header h5 {
	display: flex;
	align-items: center;
	margin-right: 8px;
}

.discount-container .step-header .ic {
	width: 1.48vh;
	height: 1.48vh;
	margin-right: 4px;
}

.step[past],
.step[past] .step-header {
	height: 5.93vh;
	opacity: 1;
}

.step-header h5 {
	text-transform: uppercase;
	font-size: 1.48vh;
	font-weight: 900;
}

.step-header .step-details {
	font-family: 'Inter';
	margin-top: 2px;
}

.step-header .btn {
	display: inline-flex;
	justify-content: center;
	align-items: center;
	font-family: 'Inter';
	font-weight: bold;
	padding: 0 22px;
	height: 28px;
	line-height: 20px;
}

.step-header .btn .ic {
	width: 1.3vh;
	height: 1.3vh;
	margin-right: 4px;
}

.step-header i:not(.ic) {
	position: absolute;
	right: 100%;
	font-style: normal;
	margin-right: 1.04vw;
}

.shipping {
	margin-bottom: 1.48vh;
}

.cards {
	font-family: 'Inter';
}

.method {
	display: flex;
	height: 5.74vh;
	justify-content: space-between;
	background: #E9E9E9;
	color: #221F1F;
	padding: 0 2.7vw 0 1.04vw;
	margin-bottom: 2px;
}

.method[selected] {
	background: #208E7F;
	color: #fff;
}

.method .method-info {
	flex-grow: 1;
	margin-left: .52vw;
}

.method .method-name {
	margin-bottom: .74vh;
}

.method .method-name,
.method .method-rate,
.method .radio {
	font-weight: 900;
	font-size: 1.48vh;
	margin-top: .93vh;
	line-height: 1;
}

.method .radio {
	position: relative;
	width: 1.48vh;
	height: 1.48vh;
	border-radius: 50%;
	border: 1px solid #221F1F;
	background: #fff;
	padding: 0;
}

.method .radio[checked] {
	border-color: #fff;
	background: none;
}

.method .radio[checked]:after {
	content: '';
	position: absolute;
	top: 2px;
	left: 2px;
	right: 2px;
	bottom: 2px;
	border-radius: 50%;
	background: #fff;
}

.method .method-description {
	font-family: 'Inter';
	opacity: .77;
}

.cards .cards-list {
	margin-top: 1.11vh;
	margin-bottom: 1.85vh;
}

.cards .card {
	height: 2.96vh;
	margin-right: .52vw;
}

.validation p {
	margin: 0 0 1.48vh 1.04vw;
	width: 26.15vw;
	font-family: 'Inter';
}

.items th,
.items td {
	border-bottom: 1px solid rgba(255,255,255,.46);
	padding: 1.11vh 0;
	vertical-align: top;
}

.items tr:last-child td {
	border: none;
}

.item .item-info {
	padding: 1.11vh .625vw;
}

.item .item-image {
	width: 96px;
}

.item .item-image img {
	display: block;
	width: 100%;
}

.item .item-name {
	font-size: 1.85vh;
	font-weight: 600;
	margin-top: 1.66vh;
}

.item .item-name:after {
	content: '';
	display: block;
	width: 10.42vw;
	height: 3px;
	margin: .93vh 0;
	background: #208E7F;
}

.item .item-variant {
	margin-bottom: 2px;
	font-family: 'Inter';
}

.item .item-quantity {
	font-family: 'Inter';
}

#cardErrors {
	margin-top: 12px;
	color: #ea2c3c;
	font-weight: bold;
	font-size: 12px;
}

[paid] #paymentForm {
	display: none;
}

::-webkit-input-placeholder {
	/* Chrome/Opera/Safari */
	color: #221f1f;
	opacity: 0.46;
}
::-moz-placeholder {
	/* Firefox 19+ */
	color: #221f1f;
	opacity: 0.46;
}
:-ms-input-placeholder {
	/* IE 10+ */
	color: #221f1f;
	opacity: 0.46;
}
:-moz-placeholder {
	/* Firefox 18- */
	color: #221f1f;
	opacity: 0.46;
}

img.logo {
	width: 100%;
	display: block;
	margin-bottom: 16px;
}

.page-title {
	font-size: 18px;
	position: relative;
	color: rgba(250, 250, 250, 0.77);
}

.page-title:after {
	content: "";
	position: absolute;
	top: 14px;
	bottom: 0;
	right: 0;
	left: 175px;
	height: 2px;
	background: #fafafa;
}

.confirmation > .btn {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 17.7vw;
	height: 3.23vw;
	font-size: 1.04vw;
	font-weight: 900;
	margin: auto;
	background: none;
	border-color: #fff;
	padding: 0;
	white-space: nowrap;
	margin-top: 9.72vh;
	z-index: 2;
}

.confirmation > .btn .ic {
	margin-left: 12px;
	width: 1.04vw;
	height: 1.04vw;
}

.breakdown .price {
	float: right;
	margin-left: 24px;
	padding-left: 8px;
}

.iframe-container {
	min-height: 150px;
}

.breakdown {
	font-weight: 300;
	margin-top: 8px;
}

.breakdown label {
	padding-right: 8px;
}

.breakdown label,
.breakdown .price {
	background: #fff;
	position: relative;
	z-index: 1;
}

.breakdown .amount {
	position: relative;
	line-height: 1;
	margin-bottom: 12px;
}

.breakdown .amount:after {
	content: "";
	position: absolute;
	left: 0;
	right: 0;
	bottom: 1px;
	height: 1px;
	background: #221f1f;
	opacity: 0.12;
}

.breakdown .total {
	font-weight: 400;
	text-transform: uppercase;
	text-align: center;
	margin-top: 16px;
}

.breakdown .total:after {
	display: none;
}

.breakdown .total label {
	font-size: 10px;
	display: block;
	padding: 0;
	letter-spacing: 1.8px;
}

.breakdown .total .price {
	font-size: 20px;
	float: none;
	padding: 0;
	margin: 6px 0 0;
	display: block;
	letter-spacing: 3.2px;
}

.iframe-container .success {
	display: none;
}

[paid] .iframe-container .success {
	display: block;
	padding: 8px 32px 0;
	text-align: center;
	font-size: 16px;
}

[paid] .iframe-container .success h2 {
	text-transform: uppercase;
	margin: 0;
	font-size: 28px;
	color: #a1af3e;
}

.iframe-container .success .paid-btn {
	position: absolute;
	background: #a1af3e;
	color: #fff;
	text-align: center;
	line-height: 60px;
	bottom: 0;
	left: 0;
	right: 0;
	font-weight: bold;
	text-transform: uppercase;
	letter-spacing: 2px;
	font-size: 18px;
}

.iframe-container .processing {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(255, 255, 255, 0.72);
	display: none;
	z-index: 11;
}

.iframe-container[processing] {
	z-index: 10;
}

.iframe-container[processing] .processing {
	display: block;
}

.iframe-container .processing span {
	height: 90px;
	line-height: 24px;
	font-size: 24px;
	text-transform: uppercase;
	letter-spacing: 1.8px;
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	margin: auto;
	text-align: center;
}

@media screen and (max-width: 1023px) {
	.view {
		border-top: 1px solid #208E7F;
		padding: 8px 32px;
	}
	.view .view-title h1 {
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 32px;
	}
	.view .view-title h1 .ic {
		width: 32px;
		height: 32px;
		margin-right: 8px;
	}
	.container {
		flex-flow: column-reverse nowrap;
		margin-top: 20px;
	}
	.steps {
		width: auto;
		height: auto;
		margin-top: 30px;
	}
	.summary {
		width: auto;
		margin: 0;
	}
	.summary .items {
		height: auto;
	}
	.view .view-title .collection {
		display: none;
	}
	.step-header {
		height: 40px;
		padding: 0 20px;
	}
	.step-header h5 {
		font-size: 16px;
	}
	.summary .step-header h5 span {
		text-transform: none;
		font-weight: 400;
	}
	.summary .step-header h5 .ic {
		width: 12px;
		height: 12px;
		margin-left: 8px;
		transition: transform .16s ease-out;
	}
	.discount-container {
		margin-bottom: 24px;
	}
	.discount-container .field[loading]:after {
		bottom: 16px;
	}
	.discount-container .step-header {
		margin-bottom: 8px;
	}
	.discount-container .step-header h5 .ic {
		margin-left: 0;
	}
	.discount-container .step-header span {
		font-size: 12px;
	}
	.summary .step-header .step-total {
		font-weight: 900;
	}
	.step-header i:not(.ic) {
		position: relative;
		right: 0;
	}
	.step-header .step-details {
		font-size: 12px;
	}
	.summary .total-line {
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-size: 20px;
		padding: 8px 0;
	}
	.summary .summary-content {
		position: relative;
		transition: height .16s ease-out;
		overflow: hidden;
	}
	.summary .summary-content:not([opened]) {
		height: 0 !important;
	}
	.summary .btn {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 92px;
		height: 28px;
		line-height: 20px;
		padding: 0;
		margin: auto;
		font-size: 12px;
		text-transform: uppercase;
		font-weight: bold;
	}
	.summary .btn .ic {
		width: 14px;
		height: 14px;
		margin-right: 4px;
	}
	.item td {
		padding: 12px 0;
		border-bottom: 1px solid #208E7F;
	}
	.item .item-info {
		position: relative;
		padding: 20px 16px 12px;
	}
	.item .item-name {
		font-size: 13px;
	}
	.item .item-name:after {
		width: 192px;
		margin: 12px 0;
	}
	.item .item-image {
		width: 96px;
	}
	.summary .total-line .next-step {
		font-size: 12px;
	}
	.item .item-variant,
	.item .item-quantity {
		font-size: 12px;
		margin-bottom: 2px;
	}
	.fieldgroup {
		display: block;
		margin: 0;
	}
	.field input, .field select, .card-input {
		height: 40px;
	}
	.fieldgroup .field {
		margin: 0 0 20px;
	}
	.fieldgroup .field .newsletter {
		margin-top: 20px;
	}
	.fieldgroup .field.m {
		width: 100%;
	}
	.fieldgroup.mobile {
		display: flex;
		flex-flow: row wrap;
	}
	.fieldgroup .field.s {
		width: 48%;
	}
	.fieldgroup .field.s:first-child {
		width: 100%;
	}
	.steps .step {
		height: 40px;
	}
	.step[past], .step[past] .step-header {
		height: 70px;
	}
	.method {
		height: 60px;
		padding: 0 8px;
	}
	.method .method-info {
		margin-left: 8px;
	}
	.method .method-name,
	.method .method-rate,
	.method .radio {
		font-size: 13px;
	}
	.method .radio {
		width: 14px;
		height: 14px;
	}
	.btn.large {
		width: 100%;
		height: 62px;
		font-size: 20px;
	}
	.card-input {
		padding-top: 12px;
	}
	.validation p {
		width: auto;
		margin: 0 16px 12px;
	}
	.btn.large .small {
		font-size: 12px;
	}
	.invoice {
		width: auto;
		margin-top: 0;
	}
	.invoice .invoice-title {
		font-size: 18px;
	}
	.invoice h3 {
		font-size: 28px;
	}
	.invoice .invoice-details {
		padding: 24px 58px;
	}
	.invoice .invoice-total .total-label {
		font-size: 10px;
	}
	.invoice .invoice-paid {
		height: 60px;
		font-size: 18px;
	}
	.invoice .invoice-paid .ic {
		width: 28px;
		height: 28px;
		margin-right: 8px;
	}
	.invoice .invoice-total .total-amount {
		font-size: 20px;
	}
	.invoice .invoice-totals {
		margin-top: 48px;
		margin-bottom: 24px;
	}
	.invoice .invoice-title:after {
		margin: 8px auto 24px;
	}
	.invoice .invoice-line {
		margin-bottom: 8px;
	}
	.confirmation {
		padding-top: 24px;
	}
	.confirmation > .btn {
		width: 100%;
		height: 62px;
		font-size: 20px;
		line-height: 52px;
		margin-top: 48px;
	}
	.confirmation > .btn .ic {
		width: 20px;
		height: 20px;
	}
	.back-btn {
		display: inline-flex;
		align-items: center;
		font-size: 16px;
		font-weight: bold;
		font-family: 'Inter';
		margin-top: 42px;
	}
	.back-btn .ic {
		width: 14px;
		height: 14px;
		margin-right: 8px;
	}
}

@media screen and (max-width: 410px) {
	.view {
		padding: 8px 24px;
	}
	.step-header {
		padding: 0 12px;
	}
	.step-header h5 {
		font-size: 12px;
	}
	.summary .step-header h5 span,
	.fieldgroup .field,
	.method .method-description {
		font-size: 12px;
	}
	.summary .total-line {
		font-size: 16px;
	}
	.btn.large {
		font-size: 16px;
	}
	.step-header .btn {
		font-size: 10px;
	}
}
</style>

